import { motion } from "framer-motion";
import { BadgeCheck, Camera, PackageOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { waLink } from "@/lib/api";

const PARTS_IMG =
  "https://images.unsplash.com/photo-1753908750553-02863700002b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwzfHxjYXIlMjBlbmdpbmUlMjBkZXRhaWxzJTIwbWFjcm8lMjBwcmVtaXVtfGVufDB8fHx8MTc4MTA1OTU4M3ww&ixlib=rb-4.1.0&q=85";

const BADGES = [
  { icon: BadgeCheck, label: "Verifiable Part Numbers" },
  { icon: Camera, label: "Photo Proof Before Fitting" },
  { icon: PackageOpen, label: "Old Parts Returned to You" },
];

export default function GenuineParts() {
  return (
    <section id="genuine-parts" className="py-16 sm:py-28 px-5 lg:px-8 bg-white" data-testid="genuine-parts-section">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-xs uppercase tracking-[0.25em] text-[#FF5A00] font-bold mb-3">
            Our Promise
          </p>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#0A0A0A]">
            The Only Promise That Matters:{" "}
            <span className="text-[#FF5A00]">100% Genuine Parts.</span>
          </h2>
          <p className="mt-5 text-[#52525B] leading-relaxed">
            Most garages quietly fit white-labeled or duplicate parts. At
            AutoHQ, every single part is an authentic manufacturer part with a
            verifiable part number. We show you the part and its packaging
            before fitting, and send photos of everything installed in your
            car.
          </p>
          <div className="mt-8 space-y-3">
            {BADGES.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-3 bg-white border border-[#E4E4E7] rounded-sm px-4 py-3.5 hover:border-[#FF5A00] transition-colors"
              >
                <Icon className="w-5 h-5 text-[#FF5A00] shrink-0" />
                <span className="font-semibold text-sm text-[#0A0A0A]">✓ {label}</span>
              </div>
            ))}
          </div>
          <Button
            data-testid="genuine-parts-whatsapp-cta"
            asChild
            className="mt-8 bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold px-6 py-5 rounded-sm"
          >
            <a
              href={waLink("Hi AutoHQ! I have a question about your genuine parts guarantee.")}
              target="_blank"
              rel="noopener noreferrer"
            >
              Ask Us Anything on WhatsApp
            </a>
          </Button>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative hidden lg:block"
        >
          <img
            src={PARTS_IMG}
            alt="Genuine engine parts"
            className="rounded-sm border border-[#E4E4E7] object-cover w-full h-[480px]"
          />
          <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur px-4 py-2.5 rounded-sm border border-[#E4E4E7] text-xs font-bold tracking-wide text-[#0A0A0A] uppercase">
            No white-labels. No duplicates. Ever.
          </div>
        </motion.div>
      </div>
    </section>
  );
}
